import { Button, ButtonGroup, Dropdown, type IModalProps, Modal, Text } from '@lego/klik-ui';
import { useEffect } from 'react';

export type ConfirmationSelectionDialogProps<T> = {
  header: string;
  body: string;
  selectionTitle: string;
  selectionOptions: T[];
  getOptionLabel: (option: T) => string;
  getOptionKey?: (option: T) => React.Key;
  selectedValue: T | null;
  onSelectedChange: (value: T | null) => void;
  onConfirmed: (value: T) => void;
  selectionPlaceholder?: string;
};

export function ConfirmationSelectionDialog<T>({
  header,
  body,
  isOpen,
  onClose,
  size = 'xl',
  variant = 'warning',
  selectionTitle,
  selectionOptions,
  selectionPlaceholder = 'Select an option',
  onSelectedChange,
  selectedValue,
  getOptionLabel,
  getOptionKey,
  onConfirmed,
}: ConfirmationSelectionDialogProps<T> & Omit<IModalProps, 'children'>) {
  const getOptionId = (option: T): string =>
    String(getOptionKey?.(option) ?? getOptionLabel(option));

  const handleClose = () => {
    onSelectedChange(null);
    onClose();
  };

  const handleSelectChange = (val: string) => {
    const found = selectionOptions.find((option) => getOptionId(option) === val);
    onSelectedChange((found ?? null) as T | null);
  };

  const displayedValue =
    selectedValue ?? (isOpen && selectionOptions.length === 1 ? selectionOptions[0] : null);
  const selectedId = displayedValue ? getOptionId(displayedValue) : null;

  useEffect(() => {
    if (isOpen && selectionOptions.length === 1 && !selectedValue) {
      onSelectedChange(selectionOptions[0]);
    }
  }, [selectionOptions, selectedValue, onSelectedChange, isOpen]);

  return (
    <Modal hideIcon isOpen={isOpen} onClose={handleClose} size={size} variant={variant}>
      <Modal.Overlay />

      <Modal.Content>
        <Modal.Header fontSize="xl">{header}</Modal.Header>
        <Modal.CloseButton />

        <Modal.Body fontSize="md">
          <Text mb={4}>{body}</Text>

          <Text fontSize="sm" fontWeight="bold" mb={1}>
            {selectionTitle}
          </Text>

          <Dropdown
            defaultValue={selectedId ? [selectedId] : []}
            key={`${isOpen ? 'open' : 'closed'}-${selectionOptions
              .map((option) => getOptionLabel(option))
              .join('|')}`}
            onChange={handleSelectChange}
            placeholder={selectionPlaceholder}
            shouldDisplaySelectionLabels
            type="text"
          >
            {selectionOptions.map((option) => {
              const optionId = getOptionId(option);
              return (
                <Dropdown.Option key={optionId} value={optionId}>
                  {getOptionLabel(option)}
                </Dropdown.Option>
              );
            })}
          </Dropdown>
        </Modal.Body>

        <Modal.Footer>
          <ButtonGroup>
            <Button
              colorScheme="warning"
              isDisabled={!selectedValue}
              onClick={() => selectedValue && onConfirmed(selectedValue)}
            >
              Confirm
            </Button>

            <Button onClick={handleClose} variant="outline">
              Cancel
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
